import { ReloadOutlined } from '@ant-design/icons';
import type { AuditLogRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, DatePicker, Input, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { hq } from '../api/client';
import { sumWidth } from '@hq/ui-kit';

/** 审计日志（留存与检索举证）。 */
export default function SystemAudit() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'day'), dayjs()]);
  const [bizType, setBizType] = useState('');
  const [action, setAction] = useState('');
  const [keyword, setKeyword] = useState('');
  const [size, setSize] = useState(50);

  const list = useQuery({
    queryKey: ['system', 'audit', range, bizType, action, keyword, size],
    placeholderData: keepPreviousData,
    queryFn: () =>
      hq.audit.list({
        // 用本地 yyyy-MM-dd HH:mm:ss，不用 toISOString()：后者输出 UTC，
        // 客户端与服务端时区不一致时整个区间会偏移一天，查出来的和数据对不上
        beginTime: range[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        endTime: range[1].endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        bizType: bizType || undefined,
        action: action || undefined,
        size
      })
  });

  const summary = useQuery({
    queryKey: ['system', 'audit', 'summary', range],
    queryFn: () =>
      hq.audit.summary(
        range[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        range[1].endOf('day').format('YYYY-MM-DD HH:mm:ss')
      ),
    staleTime: 60 * 1000
  });

  const rows = (list.data?.items ?? []).filter((row) => {
    const text = keyword.trim();
    if (!text) return true;
    return [row.bizNo, row.bizId, row.userName, row.remark, row.requestId].some((value) => (value ?? '').includes(text));
  });

  const columns: ColumnsType<AuditLogRow> = [
    { title: '时间', dataIndex: 'actAt', width: 170, render: (value: string) => formatDisplayTime(value) },
    { title: '业务', dataIndex: 'bizType', width: 120, render: (value: string | null) => value || '-' },
    { title: '单号', dataIndex: 'bizNo', width: 180, render: (value: string | null) => value || '-' },
    { title: '动作', dataIndex: 'action', width: 130, render: (value: string) => <Tag color={value === 'LOGIN' ? 'blue' : 'geekblue'}>{value}</Tag> },
    { title: '操作人', dataIndex: 'userName', width: 120, render: (value: string | null) => value || '-' },
    { title: '角色', dataIndex: 'roleCodes', width: 160, render: (value: string | null) => value || '-' },
    { title: '终端', dataIndex: 'terminal', width: 90, render: (value: string | null) => value || '-' },
    { title: 'IP', dataIndex: 'ip', width: 140, render: (value: string | null) => value || '-' },
    { title: '备注', dataIndex: 'remark', width: 220, ellipsis: true, render: (value: string | null) => value || '-' }
  ];

  return (
    <Card
      className="page-card"
      title={<Space>审计日志<Typography.Text type="secondary">留存 ≥6 个月，支持按业务/动作/人员检索</Typography.Text></Space>}
      extra={
        <Space>
          <DatePicker.RangePicker value={range} onChange={(value) => value && setRange(value as [Dayjs, Dayjs])} allowClear={false} />
          <Input.Search allowClear placeholder="单号 / 人员 / requestId" style={{ width: 220 }} onSearch={(value) => setKeyword(value.trim())} />
          <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} loading={list.isFetching}>
            刷新
          </Button>
        </Space>
      }
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="区间操作数" value={summary.data?.total ?? 0} loading={summary.isPending} />
          </Card>
        </Col>
        {Object.entries(summary.data?.byAction ?? {})
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([key, count]) => (
            <Col xs={12} md={6} key={key}>
              <Card size="small">
                <Statistic title={key} value={count} />
              </Card>
            </Col>
          ))}
      </Row>

      <Space style={{ marginBottom: 12 }}>
        <Input allowClear placeholder="业务类型（如 APPLY）" style={{ width: 200 }} value={bizType} onChange={(event) => setBizType(event.target.value.trim())} />
        <Input allowClear placeholder="动作（如 CREATE/SIGN）" style={{ width: 200 }} value={action} onChange={(event) => setAction(event.target.value.trim().toUpperCase())} />
      </Space>

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="审计日志加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<AuditLogRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
      />
      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.data?.hasMore ? (
          <Button onClick={() => setSize((prev) => prev + 50)} loading={list.isFetching}>
            加载更多
          </Button>
        ) : null}
      </Space>
    </Card>
  );
}
